import React from 'react'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'

import { Divider } from '@mui/material'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import { JunctionMode, NumberFilterScope, NumberOperator, NumberParcelFilterScope } from '../../tcm_scopes'
import { FieldIconButton, FieldRow, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('StringFilterView')

type NumberFilterViewProps = {
    className?: string
    scope: NumberFilterScope
}

class NumberFilterViewClass implements FCClassContext<NumberFilterViewProps> {
    // :: Fields
    scope!: NumberFilterScope
    setAnchorEl!: React.Dispatch<React.SetStateAction<HTMLElement | null>>

    onSyncState({ scope }: NumberFilterViewProps) {
        this.scope = scope
    }

    render({ className }: NumberFilterViewProps) {
        const { scope } = this

        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
        this.setAnchorEl = setAnchorEl

        const menuAndOrOpened = !!anchorEl

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <StringKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <StringKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    } //

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        //this.setAnchorEl(event.currentTarget)
        this.handleAddORClicked()
    }

    readonly handleClose = () => {
        this.setAnchorEl(null)
    }

    readonly handleAddANDClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.AND).catch(LOG.caught)
    }

    readonly handleAddORClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.setAnchorEl(null)
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const StringKeywordFilterView = classToFComponent(NumberFilterViewClass, React)
export default StringKeywordFilterView

// :: StringParcelViewClass

type NumberParcelViewProps = {
    className?: string
    acceptBlankValues: boolean
    scope: NumberParcelFilterScope
    divider?: boolean
}

class StringKeywordParcelViewClass implements FCClassContext<NumberParcelViewProps> {
    scope!: NumberParcelFilterScope

    render({ className, acceptBlankValues, scope, divider }: NumberParcelViewProps) {
        this.scope = scope

        const isRange = scope.operator === NumberOperator.in_range || scope.operator === NumberOperator.out_of_range

        return (
            <>
                <FilterPanel className={className}>
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>
                            {criterionLabel(scope.junctionMode === JunctionMode.AND)}
                        </FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${NumberOperator.is}`}
                            value={`${scope.operator}`}
                            onChange={this.handleSelectChange}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value={NumberOperator.is_less_then}>Menor que</MenuItem>
                            <MenuItem value={NumberOperator.is_less_then_or_equal_to}>Menor que ou igual</MenuItem>
                            <MenuItem value={NumberOperator.is_greater_then}>Maior que</MenuItem>
                            <MenuItem value={NumberOperator.is_greater_then_or_equal_to}>Maior que ou igual</MenuItem>
                            <MenuItem value={NumberOperator.is}>É igual</MenuItem>
                            <MenuItem value={NumberOperator.is_not}>Não é igual</MenuItem>
                            <MenuItem value={NumberOperator.in_range}>No intervalo</MenuItem>
                            <MenuItem value={NumberOperator.out_of_range}>Fora do intervalo</MenuItem>
                            {acceptBlankValues && <MenuItem value={NumberOperator.is_blank}>Está em branco</MenuItem>}
                            {acceptBlankValues && (
                                <MenuItem value={NumberOperator.is_not_blank}>Não está em branco</MenuItem>
                            )}
                        </TextField>
                    </Field>
                    {scope.valueVisible && !isRange && (
                        <Field>
                            <FieldLabel htmlFor={`${scope.uid}-valor`}>Valor</FieldLabel>
                            <TextField
                                id={`${scope.uid}-valor`}
                                size="small"
                                value={scope.value}
                                onChange={this.handleFromValueChange}
                                onKeyDown={this.handleKeyDown}
                                sx={inputSx}
                                fullWidth
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <FieldIconButton
                                                size="small"
                                                aria-label="Limpar valor"
                                                onClick={this.handleClearClick}
                                            >
                                                <DeleteIcon />
                                            </FieldIconButton>
                                        )
                                    }
                                }}
                            />
                        </Field>
                    )}
                    {scope.valueVisible && isRange && (
                        <FieldRow>
                            <Field style={{ flexGrow: 1 }}>
                                <FieldLabel htmlFor={`${scope.uid}-de`}>De</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-de`}
                                    size="small"
                                    value={scope.value}
                                    onChange={this.handleFromValueChange}
                                    onKeyDown={this.handleKeyDown}
                                    sx={inputSx}
                                    fullWidth
                                />
                            </Field>
                            <Field style={{ flexGrow: 1 }}>
                                <FieldLabel htmlFor={`${scope.uid}-ate`}>Até</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-ate`}
                                    size="small"
                                    value={scope.toValue}
                                    onChange={this.handleToValueChange}
                                    onKeyDown={this.handleKeyDown}
                                    sx={inputSx}
                                    fullWidth
                                />
                            </Field>
                            <FieldIconButton size="small" aria-label="Limpar intervalo" onClick={this.handleClearClick}>
                                <DeleteIcon />
                            </FieldIconButton>
                        </FieldRow>
                    )}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onOperatorChange(+event.target.value)
    }

    readonly handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value, 'from').catch(LOG.caught)
    }

    readonly handleToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value, 'to').catch(LOG.caught)
    }

    readonly handleClearClick = () => {
        this.scope.onClear().catch(LOG.caught)
    }

    readonly handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            this.scope.onEnterKeyPressed().catch(LOG.caught)
        }
    }
}

export const StringKeywordParcelView = classToFComponent(StringKeywordParcelViewClass, React)
